/**
 * Theme manager for handling light/dark theme switching
 */

import { lightTheme, darkTheme, applyTheme } from '../components/index.js'

export type ThemeMode = 'light' | 'dark' | 'auto'

export class ThemeManager {
  private currentMode: ThemeMode = 'auto'
  private mediaQuery: MediaQueryList
  private changeListeners: Set<(mode: ThemeMode, isDark: boolean) => void> = new Set()

  constructor() {
    this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    this.mediaQuery.addEventListener('change', this.handleSystemThemeChange.bind(this))
  }

  /**
   * Set the theme mode
   */
  setTheme(mode: ThemeMode): void {
    this.currentMode = mode
    this.applyCurrentTheme()
    this.notifyListeners()
  }

  /**
   * Get the current theme mode
   */
  getCurrentMode(): ThemeMode {
    return this.currentMode
  }

  /**
   * Check if the current effective theme is dark
   */
  isDark(): boolean {
    if (this.currentMode === 'auto') {
      return this.mediaQuery.matches
    }
    return this.currentMode === 'dark'
  }

  /**
   * Add a listener for theme changes
   */
  addChangeListener(listener: (mode: ThemeMode, isDark: boolean) => void): void {
    this.changeListeners.add(listener)
  }

  /**
   * Remove a theme change listener
   */
  removeChangeListener(listener: (mode: ThemeMode, isDark: boolean) => void): void {
    this.changeListeners.delete(listener)
  }

  /**
   * Initialize theme from settings
   */
  initializeFromSettings(mode: ThemeMode): void {
    this.currentMode = mode
    this.applyCurrentTheme()
  }

  private applyCurrentTheme(): void {
    const shouldUseDark = this.isDark()
    const theme = shouldUseDark ? darkTheme : lightTheme

    applyTheme(theme)

    // Update body class for any CSS that needs theme awareness
    document.body.classList.toggle('cairn-dark-theme', shouldUseDark)
    document.body.classList.toggle('cairn-light-theme', !shouldUseDark)
  }

  private handleSystemThemeChange(): void {
    if (this.currentMode === 'auto') {
      this.applyCurrentTheme()
      this.notifyListeners()
    }
  }

  private notifyListeners(): void {
    const isDark = this.isDark()
    this.changeListeners.forEach(listener => {
      listener(this.currentMode, isDark)
    })
  }
}

// Export singleton instance
export const themeManager = new ThemeManager()
